import { useQuery, UseQueryResult } from '@tanstack/react-query'
import {
  getBrands,
  getMaterials,
  getProducts,
  getCategories,
  searchProducts
} from '../services/api'
import { Brand } from '../services/types/brands'
import {
  Product,
  ProductsResponseWithPagination
} from '../services/types/product'
import { Material } from '../services/types/material'
import { Category } from '../services/types/category'

export const useGetBrands = (): UseQueryResult<Brand[], Error> => {
  return useQuery<Brand[], Error>({
    queryKey: ['brands'],
    queryFn: getBrands
  })
}

export const useGetProducts = ({
  brandId,
  skipToken
}: {
  brandId: string
  skipToken?: string
}): UseQueryResult<ProductsResponseWithPagination, Error> => {
  return useQuery<ProductsResponseWithPagination, Error>({
    queryKey: ['products', brandId, skipToken],
    queryFn: () =>
      brandId
        ? getProducts(brandId, skipToken)
        : Promise.reject(new Error('No brand ID provided')),
    enabled: Boolean(brandId),
    placeholderData: { products: [], nextSkipToken: '' }
  })
}

export const useSearchProducts = ({
  query,
  brandId,
  categoryId,
  materialId
}: {
  query: string | null
  brandId: string
  categoryId: string | null
  materialId: string | null
  enabled: boolean
}): UseQueryResult<Product[], Error> => {
  return useQuery<Product[], Error, Product[]>({
    queryKey: ['searchProducts', query, categoryId, materialId],
    queryFn: (): Promise<Product[]> =>
      searchProducts({
        searchTerm: query,
        brandId: brandId,
        categoryId: categoryId,
        materialId: materialId
      }),
    enabled: !!query || !!categoryId || !!materialId,
    staleTime: 0
  })
}

export const useGetCategories = (): UseQueryResult<Category[], Error> => {
  return useQuery<Category[], Error>({
    queryKey: ['categories'],
    queryFn: () => getCategories()
  })
}

export const useGetMaterials = (): UseQueryResult<Material[], Error> => {
  return useQuery<Material[], Error>({
    queryKey: ['materials'],
    queryFn: () => getMaterials()
  })
}
